import { useEffect, useState } from 'react';
import { Size } from '../../constants/commonEnums';
import { If } from '../Atoms/Conditionals';
import { Loader } from '../Atoms/Loader';
import { checkLoginStatus } from '../Header/utils';
import PaytaxServices from './paytax-service';
import { TabSwitch } from './switching';
import { taxRegimeTab } from './tax-calculator-constants';
import { CALCULATOR_DATA, TAX_REGIMES } from './types';
import { Button, ButtonVariants } from '../Atoms/Button';

export const TaxSummary = ({
  inputTabData,
  renderCalculation,
}: {
  inputTabData: CALCULATOR_DATA;
  renderCalculation: boolean;
}) => {
  const [taxData, setTaxData] = useState<TAX_REGIMES>();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const slug = 'budget_calculator_email';
  const [loggedInStatus, setLoggedInStatus] = useState<{
    status: boolean;
    mail: string | null;
  }>({
    status: false,
    mail: null,
  });

  useEffect(() => {
    handleTaxCall();
  }, [renderCalculation]);

  const commonProps = {
    taxCalculation: taxData,
    activeTab: activeTab,
  };

  const handleEmailSubmission = () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setMessage({ type: 'error', text: 'Invalid email address' });
      return;
    }
    handleTaxCall(email)
      .then(() => {
        setMessage({ type: 'success', text: 'Email sent successfully' });
        setTimeout(() => setMessage(null), 5000);
      })
      .catch(() => {
        setMessage({ type: 'error', text: 'Failed to send email' });
      });
  };

  const handleTaxCall = async (email?: string) => {
    try {
      const result = await PaytaxServices.getCalculation(
        '/f/itr/tax_calculator_results/',
        inputTabData
      );
      if (email) {
        await PaytaxServices.sendTaxMail(email, result, slug);
      }

      if (loggedInStatus && loggedInStatus?.mail) {
        await PaytaxServices.sendTaxMail(loggedInStatus.mail, result, slug);
      } else {
        const data = await checkLoginStatus();
        if (data?.isloggedin) {
          try {
            await PaytaxServices.sendTaxMail(data.username, result, slug);
          } catch (mailError) {
            console.error('Error sending tax mail:', mailError);
          }
          setLoggedInStatus({
            status: data.isloggedin || false,
            mail: data.username || null,
          });
        }
      }
      setTaxData(result);
    } catch (error) {
      console.error('Error calling API:', error);
    }
  };

  if (!taxData) {
    return (
      <div className="flex justify-center text-s-20 h-96 sm:h-56 mt-7">
        <Loader
          loadingLabel="Calculating your tax details..."
          size={Size.XLARGE}
          loaderUrl={
            'https://assets1.cleartax-cdn.com/cleartax-gst/images/1595828806_pinchy_attention.gif'
          }
        />
      </div>
    );
  }

  return (
    <>
      <div className="gap-20 sm:block tax-graph-wrapper">
        <If condition={!!taxData}>
          <TabSwitch
            tabsData={taxRegimeTab}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            classes="md:w-[239px]"
            commonProps={commonProps}
            taxData={taxData}
          />
        </If>
        <div className="bg-[#D0E4FE33] px-12 sm:px-4 py-2 w-full mt-4 ">
          <div className="text-s-14 rounded-md text-center w-full flex gap-4 justify-center items-center sm:block">
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              placeholder="Enter your email to get tax result"
              className="my-2 p-2 border border-gray-300 rounded-md w-full"
            />
            <Button
              variant={ButtonVariants.bluefilled}
              className="p-2 text-s-14 px-6 sm:w-full sm:mb-2"
              onClick={handleEmailSubmission}
            >
              Submit
            </Button>
          </div>
          {message && (
            <span
              className={`text-s-14 ${
                message.type === 'success' ? 'text-green-400' : 'text-red-450'
              }`}
            >
              {message.text}
            </span>
          )}
        </div>
      </div>
    </>
  );
};
